import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaImage, FaPaperPlane } from "react-icons/fa";
import { useAuth } from "../Login/AuthContext";

const ALERT_COLOR = "#92400e";
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const SolicitudCuadroPersonalizado = () => {
  const { usuario, token } = useAuth();
  const [nombreCompleto, setNombreCompleto] = useState(
    [usuario?.nombre, usuario?.apellido].filter(Boolean).join(" ") || usuario?.username || ""
  );
  const [correoElectronico, setCorreoElectronico] = useState(usuario?.email || "");
  const [telefono, setTelefono] = useState(usuario?.telefono || "");
  const [tamanoCuadro, setTamanoCuadro] = useState("");
  const [tamanoOtro, setTamanoOtro] = useState("");
  const [tipoSoporte, setTipoSoporte] = useState("");
  const [cambiosFoto, setCambiosFoto] = useState("");
  const [comentariosAdicionales, setComentariosAdicionales] = useState("");
  const [tiempoEntrega, setTiempoEntrega] = useState("");
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [imagen, setImagen] = useState(null);
  const [preview, setPreview] = useState("");
  const [enviando, setEnviando] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const seleccionarImagen = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!IMAGE_TYPES.includes(file.type)) {
      mostrarError("La imagen debe ser JPG, PNG o WEBP.");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      mostrarError("La imagen no puede superar los 10 MB.");
      event.target.value = "";
      return;
    }

    if (preview) URL.revokeObjectURL(preview);
    setImagen(file);
    setPreview(URL.createObjectURL(file));
  };

  const validar = () => {
    if (nombreCompleto.trim().length < 3) {
      mostrarError("Ingresa tu nombre completo.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correoElectronico.trim())) {
      mostrarError("Ingresa un correo electronico valido.");
      return false;
    }
    if (telefono.trim().length < 6 || telefono.trim().length > 30) {
      mostrarError("Ingresa un telefono valido.");
      return false;
    }
    if (!imagen) {
      mostrarError("Adjunta una foto de referencia.");
      return false;
    }
    if (!tamanoCuadro) {
      mostrarError("Selecciona el tamano del cuadro.");
      return false;
    }
    if (tamanoCuadro === "Otro" && tamanoOtro.trim().length < 2) {
      mostrarError("Indica el tamano del cuadro.");
      return false;
    }
    if (!tipoSoporte) {
      mostrarError("Selecciona el tipo de soporte.");
      return false;
    }
    if (cambiosFoto.trim().length < 10) {
      mostrarError("Contanos que cambios te gustaria hacer en la foto.");
      return false;
    }
    if (!tiempoEntrega.trim()) {
      mostrarError("Indica el tiempo de entrega deseado.");
      return false;
    }
    if (!aceptaTerminos) {
      mostrarError("Debes aceptar los terminos y condiciones para enviar la solicitud.");
      return false;
    }
    if (crearDescripcion().length > 2000) {
      mostrarError("La informacion no puede superar los 2000 caracteres.");
      return false;
    }
    return true;
  };

  const crearDescripcion = () => {
    const tamano = tamanoCuadro === "Otro" ? `Otro: ${tamanoOtro.trim()}` : tamanoCuadro;
    return [
      `Nombre completo: ${nombreCompleto.trim()}`,
      `Correo electronico: ${correoElectronico.trim()}`,
      `Telefono: ${telefono.trim()}`,
      `Tamano del cuadro: ${tamano}`,
      `Tipo de soporte: ${tipoSoporte}`,
      `Cambios en la foto: ${cambiosFoto.trim()}`,
      `Comentarios adicionales: ${comentariosAdicionales.trim() || "Sin comentarios"}`,
      `Tiempo de entrega deseado: ${tiempoEntrega.trim()}`,
      "Acepto los terminos y condiciones: Si",
    ].join("\n");
  };

  const enviarSolicitud = async (event) => {
    event.preventDefault();
    if (!validar()) return;

    const formData = new FormData();
    formData.append("descripcion", crearDescripcion());
    formData.append("numero_contacto", telefono.trim());
    formData.append("imagen", imagen);

    try {
      setEnviando(true);
      await axios.post(`${API_URL}/cuadros-personalizados`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setNombreCompleto("");
      setCorreoElectronico("");
      setTelefono("");
      setTamanoCuadro("");
      setTamanoOtro("");
      setTipoSoporte("");
      setCambiosFoto("");
      setComentariosAdicionales("");
      setTiempoEntrega("");
      setAceptaTerminos(false);
      setImagen(null);
      if (preview) URL.revokeObjectURL(preview);
      setPreview("");
      document.getElementById("imagen-cuadro-personalizado").value = "";

      await Swal.fire({
        icon: "success",
        title: "Solicitud enviada",
        text: "La artista recibio tu idea y podra comunicarse contigo.",
        confirmButtonColor: ALERT_COLOR,
      });
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.msg;
      mostrarError(message || "No se pudo enviar la solicitud. Intenta nuevamente.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className="min-h-screen bg-stone-50 px-4 py-8 md:px-8">
      <div className="mx-auto grid w-full max-w-6xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-2xl bg-stone-900 p-7 text-white shadow-xl md:p-10">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-300">
            Una obra hecha para ti
          </span>
          <h1 className="mt-4 text-3xl font-extrabold md:text-5xl">
            Personaliza tu cuadro
          </h1>
          <p className="mt-5 leading-7 text-stone-300">
            Envia una referencia y conta como imaginas la pieza. La artista revisara
            la propuesta y se comunicara contigo para confirmar posibilidades,
            medidas, tiempos y presupuesto.
          </p>

          <div className="mt-8 rounded-xl border border-stone-700 bg-stone-800 p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-stone-400">
              Solicitud de
            </p>
            <p className="mt-2 font-semibold">
              {usuario?.nombre || usuario?.username}
              {usuario?.apellido ? ` ${usuario.apellido}` : ""}
            </p>
            {usuario?.email && <p className="mt-1 text-sm text-stone-300">{usuario.email}</p>}
          </div>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-xl md:p-9">
          <h2 className="text-2xl font-extrabold text-stone-900">
            Solicita tu cuadro personalizado
          </h2>
          <p className="mt-2 text-sm text-stone-600">Todos los campos son obligatorios.</p>

          <form onSubmit={enviarSolicitud} className="mt-7 space-y-5">
            <Campo numero="1" label="Nombre completo">
              <input
                type="text"
                value={nombreCompleto}
                onChange={(event) => setNombreCompleto(event.target.value)}
                maxLength="120"
                placeholder="Tu nombre y apellido"
                className={fieldClassName}
                required
              />
            </Campo>

            <Campo numero="2" label="Correo electronico">
              <input
                type="email"
                value={correoElectronico}
                onChange={(event) => setCorreoElectronico(event.target.value)}
                maxLength="160"
                placeholder="tuemail@ejemplo.com"
                className={fieldClassName}
                required
              />
            </Campo>

            <Campo numero="3" label="Telefono">
              <input
                type="tel"
                value={telefono}
                onChange={(event) => setTelefono(event.target.value)}
                maxLength="30"
                placeholder="Ej.: +598 99 123 456"
                className={fieldClassName}
                required
              />
            </Campo>

            <Campo numero="4" label="Adjuntar foto">
              <label
                htmlFor="imagen-cuadro-personalizado"
                className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-stone-300 bg-stone-50 px-5 py-7 text-center transition hover:border-amber-700 hover:bg-amber-50"
              >
                <FaImage className="text-3xl text-amber-800" />
                <span className="mt-3 text-sm font-semibold text-stone-800">
                  {imagen ? imagen.name : "Seleccionar imagen"}
                </span>
                <span className="mt-1 text-xs text-stone-500">JPG, PNG o WEBP. Maximo 10 MB.</span>
              </label>
              <input
                id="imagen-cuadro-personalizado"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={seleccionarImagen}
                className="sr-only"
                required
              />
            </Campo>

            {preview && (
              <img
                src={preview}
                alt="Vista previa de la referencia"
                className="max-h-80 w-full rounded-xl border border-stone-200 object-contain"
              />
            )}

            <Campo numero="5" label="Tamano del cuadro">
              <Opciones
                name="tamano-cuadro"
                value={tamanoCuadro}
                onChange={setTamanoCuadro}
                options={["Pequeno", "Mediano", "Grande", "Otro"]}
              />
              {tamanoCuadro === "Otro" && (
                <input
                  type="text"
                  value={tamanoOtro}
                  onChange={(event) => setTamanoOtro(event.target.value)}
                  maxLength="80"
                  placeholder="Indica el tamano"
                  className={`${fieldClassName} mt-3`}
                  required
                />
              )}
            </Campo>

            <Campo numero="6" label="Tipo de soporte">
              <Opciones
                name="tipo-soporte"
                value={tipoSoporte}
                onChange={setTipoSoporte}
                options={["Canvas", "Papel", "Madera"]}
              />
            </Campo>

            <Campo numero="7" label="Cambios en la foto">
              <textarea
                value={cambiosFoto}
                onChange={(event) => setCambiosFoto(event.target.value)}
                maxLength="700"
                rows="4"
                placeholder="Escribe aqui lo que te gustaria cambiar"
                className={fieldClassName}
                required
              />
            </Campo>

            <Campo numero="8" label="Comentarios adicionales">
              <textarea
                value={comentariosAdicionales}
                onChange={(event) => setComentariosAdicionales(event.target.value)}
                maxLength="500"
                rows="3"
                placeholder="Cualquier otra informacion importante"
                className={fieldClassName}
              />
            </Campo>

            <Campo numero="9" label="Tiempo de entrega deseado">
              <input
                type="text"
                value={tiempoEntrega}
                onChange={(event) => setTiempoEntrega(event.target.value)}
                maxLength="120"
                placeholder="Ej.: antes del 15 de julio"
                className={fieldClassName}
                required
              />
            </Campo>

            <label className="flex items-start gap-3 rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm font-semibold text-stone-800">
              <input
                type="checkbox"
                checked={aceptaTerminos}
                onChange={(event) => setAceptaTerminos(event.target.checked)}
                className="mt-1 h-4 w-4 accent-stone-900"
                required
              />
              <span>10. Acepto los terminos y condiciones</span>
            </label>

            <button
              type="submit"
              disabled={enviando}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-stone-900 px-5 py-4 font-bold text-white transition hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaPaperPlane />
              {enviando ? "Enviando solicitud..." : "Enviar a la artista"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Campo = ({ numero, label, children }) => (
  <div>
    <label className="mb-2 block text-sm font-bold text-stone-800">
      {numero}. {label}
    </label>
    {children}
  </div>
);

const Opciones = ({ name, value, onChange, options }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((option) => (
      <label
        key={option}
        className={`cursor-pointer rounded-lg border px-4 py-2 text-sm font-semibold transition ${
          value === option
            ? "border-stone-900 bg-stone-900 text-white"
            : "border-stone-300 bg-white text-stone-700 hover:border-amber-700"
        }`}
      >
        <input
          type="radio"
          name={name}
          value={option}
          checked={value === option}
          onChange={() => onChange(option)}
          className="sr-only"
          required
        />
        {option}
      </label>
    ))}
  </div>
);

const fieldClassName =
  "w-full rounded-lg border border-stone-300 bg-white p-3 text-stone-900 outline-none transition focus:border-amber-700 focus:ring-2 focus:ring-amber-200";

const mostrarError = (text) =>
  Swal.fire({ icon: "error", title: "Revisa la solicitud", text, confirmButtonColor: ALERT_COLOR });

export default SolicitudCuadroPersonalizado;
